import { FC, useMemo } from 'react'
import { Autocomplete, Chip, TextField } from '@mui/material'
import ReactDatePicker from 'react-datepicker'
import dayjs from 'dayjs'
import SelectV2 from 'ui/components/form-components-v2/select-v2/SelectV2'
import { DEFAULT_MENU_PROPS } from 'ui/components/form-components-v2/select-v2/selectV2.configs'
import { JiraAssignableUsers, JiraCreateField } from 'models'
import { fieldKind, FieldOption, isEmptyValue, optionsOf } from './jiraFields'

// The app modal sits at z-index 10002, so its menus must render above it.
const MENU_PROPS = { ...DEFAULT_MENU_PROPS, sx: { zIndex: 10003 } }

const DATE_VALUE_FORMAT = 'YYYY-MM-DD'

const renderMultiValue = (options: FieldOption[], placeholder: string) => (selected: unknown) => {
  const values = Array.isArray(selected) ? (selected as string[]) : []
  if (!values.length) return placeholder
  if (values.length > 1) return `${values.length} selected`
  return options.find((o) => o.value === values[0])?.label ?? values[0]
}

interface Props {
  field: JiraCreateField
  value: any
  users: JiraAssignableUsers | null
  onChange: (value: any) => void
}

const JiraFieldInput: FC<Props> = ({ field, value, users, onChange }) => {
  const kind = fieldKind(field)
  const id = `jira-field-${field.id}`
  const placeholder = `${kind === 'text' || kind === 'textarea' || kind === 'number' ? 'Enter' : 'Select'} ${
    field.label
  }`

  const options = useMemo<FieldOption[]>(() => {
    if (kind === 'user' || kind === 'multiuser') {
      return (users?.values || []).map((u) => ({ label: u.display_name, value: u.account_id }))
    }
    return optionsOf(field)
  }, [field, kind, users])

  if (kind === 'select' || kind === 'user') {
    return (
      <SelectV2
        MenuProps={MENU_PROPS}
        id={id}
        label=""
        className={isEmptyValue(value) ? 'is-empty' : undefined}
        placeholder={placeholder}
        searchable={options.length > 8}
        value={value ?? ''}
        options={options}
        emptyMessage={kind === 'user' && users?.warning ? users.warning : 'No options available'}
        onChange={(e) => onChange(String(e.target.value))}
      />
    )
  }

  if (kind === 'multiselect' || kind === 'multiuser') {
    return (
      <SelectV2
        MenuProps={MENU_PROPS}
        id={id}
        label=""
        className={isEmptyValue(value) ? 'is-empty' : undefined}
        placeholder={placeholder}
        multiple
        searchable={options.length > 8}
        value={Array.isArray(value) ? value : []}
        options={options}
        emptyMessage={kind === 'multiuser' && users?.warning ? users.warning : 'No options available'}
        renderValue={renderMultiValue(options, placeholder)}
        onChange={(e) => {
          const next = e.target.value
          onChange(typeof next === 'string' ? next.split(',') : next)
        }}
      />
    )
  }

  if (kind === 'labels') {
    return (
      <Autocomplete
        multiple
        freeSolo
        options={[] as string[]}
        value={Array.isArray(value) ? value : []}
        onChange={(_, next) => onChange((next as string[]).map((v) => v.trim().replace(/\s+/g, '_')).filter(Boolean))}
        renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => <Chip size="small" label={tag} {...getTagProps({ index })} key={tag} />)
        }
        renderInput={(params) => (
          <TextField {...params} id={id} placeholder={value?.length ? '' : `Type ${field.label} and press Enter`} />
        )}
      />
    )
  }

  if (kind === 'date' || kind === 'datetime') {
    const selected = value && dayjs(value).isValid() ? dayjs(value).toDate() : null
    return (
      <ReactDatePicker
        id={id}
        selected={selected}
        onChange={(date: Date | null) => onChange(date ? dayjs(date).format(DATE_VALUE_FORMAT) : '')}
        dateFormat="MM/dd/yy"
        placeholderText="MM/DD/YY"
        formatWeekDay={(day) => day.charAt(0)}
        popperClassName="ag-custom-component-popup"
        popperPlacement="bottom-start"
        // Fixed positioning keeps the calendar from being clipped by the scrolling step body.
        popperProps={{ strategy: 'fixed' }}
        wrapperClassName="date-input"
        autoComplete="off"
        customInput={<TextField fullWidth />}
      />
    )
  }

  return (
    <TextField
      id={id}
      fullWidth
      type={kind === 'number' ? 'number' : 'text'}
      multiline={kind === 'textarea'}
      minRows={kind === 'textarea' ? 3 : undefined}
      placeholder={placeholder}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

export default JiraFieldInput
